import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Space, SpaceInput } from '@app/core/model/entities/asset/space';
import { Document } from '@app/core/model/entities/document/document';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { DocumentsService } from '@app/features/main/views/organization-documents/documents.service';
import { eq, orGroup } from '@app/shared/extra/filter-model-builder';
import { thenReturn } from '@app/shared/extra/utils';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, Observable, of, Subject, tap } from 'rxjs';
import { map, mergeMap, reduce, switchMap } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class SpacesService {
  private readonly apiService = inject(ApiService);
  private readonly appManager = inject(AppManager);
  private readonly appConfig = inject(AppConfig);
  private readonly documentsService = inject(DocumentsService);
  private readonly assetsService = inject(AssetsService);

  private sidePanelToggleSubject = new Subject<Space | null>();

  private addSpaceSubject = new Subject<Space>();
  private updateSpacesSubject = new Subject<Space[]>();
  private deleteSpaceSubject = new Subject<{ deletedSpace: Space, deleteChildren: boolean }>();
  private deleteSpacesSubject = new Subject<string[]>();

  private readonly spaceInfoGraphqlFragment = gql`
    fragment SpaceInfo on Space {
      id
      name
      identifier
      parentPathList
      assetId
      properties
      computedProperties
      creationDate
      creationActor {
        actorId
        name
      }
      lastChangeDate
      lastChangeActor {
        actorId
        name
      }
      dataDate
    }
  `;

  /**
   * Emits a Space once it has been created.
   */
  public get spaceAdded$(): Observable<Space> {
    return this.addSpaceSubject.asObservable();
  }

  /**
   * Emits Spaces whenever they have been updated.
   */
  public get spacesUpdated$(): Observable<Space[]> {
    return this.updateSpacesSubject.asObservable();
  }

  /**
   * Emits a Space after it has been deleted.
   */
  public get spaceDeleted$(): Observable<{ deletedSpace: Space, deleteChildren: boolean }> {
    return this.deleteSpaceSubject.asObservable();
  }

  /**
   * Emits Spaces whenever they have been deleted.
   */
  public get spacesDeleted$(): Observable<string[]> {
    return this.deleteSpacesSubject.asObservable();
  }

  /**
   * Emits a Space whenever the side panel opens, or null whenever it closes.
   */
  public get sidePanelToggle$(): Observable<Space | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Fetch Space belonging to a spaceId.
   * @param spaceId ID of the Space to return.
   * @return Space
   */
  public loadSpace(spaceId: string): Observable<Space> {
    const query = gql`
      query Space($spaceId: String!) {
        space(id: $spaceId) {
          ...SpaceInfo
        }
      }
      ${this.spaceInfoGraphqlFragment}
    `;
    const variables = {spaceId};
    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Space, data['space'])));
  }


  /**
   * Loads a spaces page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch spaces based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the spaces.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Space objects, each with additional user information.
   */
  public loadSpacesPage(selectParams: SelectParams): Observable<Space[]> {
    const query = gql`
      query Spaces($organizationId: String!, $selectParams: SelectParams!) {
        spaces(organizationId: $organizationId, selectParams: $selectParams) {
          ...SpaceInfo
        }
      }
      ${this.spaceInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Space, data['spaces'] as Space[])));
  }

  /**
   * Loads all spaces pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch spaces based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the spaces.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Space objects, each with additional user information.
   */
  public loadAllSpaces(selectParams: SelectParams): Observable<Space[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadSpacesPage(params)
      .pipe(
        expand((spaces, i) => {
          if (spaces.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadSpacesPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Space[]),
        tap(spaces => {
          // Set hasChildren flag
          spaces.forEach(space => {
            space.hasChildren = spaces.some(otherSpace => otherSpace.parentPath.includes(space.id));
          });
        })
      );
  }

  /**
   * Fetch the root space of an asset.
   * @param assetId ID of the asset to which get the root space.
   * @return Space root related to an asset Space.
   */
  public loadAssetRootSpace(assetId: string): Observable<Space> {
    const query = gql`
      query RootSpace($assetId: String!) {
        rootSpace(assetId: $assetId) {
          ...SpaceInfo
        }
      }
      ${this.spaceInfoGraphqlFragment}
    `;
    const variables = {assetId};
    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Space, data['rootSpace'])));
  }

  /**
   * Create new Space in an Asset and within the given parent Space.
   * @param spaceInput New Space's information.
   * @return Created Space.
   */
  public createSpace(spaceInput: SpaceInput): Observable<Space> {
    const mutation = gql`
      mutation CreateSpace($spaceInput: CreateSpaceInput!) {
        createSpace(spaceInput: $spaceInput) {
          ...SpaceInfo
        }
      }
      ${this.spaceInfoGraphqlFragment}
    `;
    const variables = {spaceInput};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Space, data['createSpace'])),
        switchMap(newSpace => {
          newSpace.hasChildren = false;
          this.addSpaceSubject.next(newSpace);

          // Refresh parent Spaces
          return this.getRelatedSpacesById(newSpace.id)
            .pipe(
              map(updatedSpaces => {
                // Set hasChildren flag
                updatedSpaces.forEach(space => {
                  space.hasChildren = updatedSpaces.some(otherSpace => otherSpace.parentPath.includes(space.id));
                });
                this.updateSpacesSubject.next(updatedSpaces);
                return newSpace;
              })
            );
        })
      );
  }

  /**
   * Call the API to update a Space.
   * @param space Space to update.
   * @param spaceInput Data for updating the Space.
   * @param moveChildren If true, children will be moved with the space, otherwise they'll be reattached to the space's current parent.
   * @return Updated Space.
   */
  public updateSpace(space: Space, spaceInput: SpaceInput, moveChildren: boolean = false): Observable<Space> {
    const mutation = gql`
      mutation UpdateSpace($spaceInput: UpdateSpaceInput!) {
        updateSpace(spaceInput: $spaceInput) {
          ...SpaceInfo
        }
      }
      ${this.spaceInfoGraphqlFragment}
    `;
    const variables = {
      spaceInput: {
        id: space.id,
        name: space.name,
        parentPath: space.parentPath,
        moveSpaceChildren: moveChildren,
        ...spaceInput
      }
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Space, data['updateSpace'])),
        switchMap(updatedSpace => {
          // Refresh updated spaces depending on if the parentPath was updated or not
          const updatedSpaces = spaceInput.hasOwnProperty('parentPath')
            ? this.getUpdatedSpacesAfterMovingSpace(space.id, space.parentPath.last())
            : this.getRelatedSpacesById(updatedSpace.id);

          return updatedSpaces
            .pipe(
              map(updatedSpaces => {
                this.updateSpacesSubject.next(updatedSpaces);
                return updatedSpace;
              })
            );
        })
      );
  }

  /**
   * Call the API to update a list of Space.
   * @param spaces A list of object spaces to update
   * @return Updated Spaces.
   */
  public updateSpaces(spaces: { space: Space, spaceInput: SpaceInput }[]): Observable<Space[]> {
    const mutation = gql`
      mutation UpdateSpaces($spaceInputs: [UpdateSpaceInput!]!){
        updateSpaces(spaceInputs: $spaceInputs){
          ...SpaceInfo
        }
      }
      ${this.spaceInfoGraphqlFragment}
    `;
    const variables = {
      spaceInputs: spaces.map(({space: {id, name, parentPath}, spaceInput}) => {
        return {
          id,
          name,
          parentPath,
          moveSpaceChildren: false,
          ...spaceInput
        };
      })
    };
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Space, data['updateSpaces'] as Space[])),
        tap(updatedSpaces => this.updateSpacesSubject.next(updatedSpaces))
      );
  }

  /**
   * Call the API to delete the given Space. If the Space has children, they can either be deleted as well, or moved
   * to the deleted Space's parent.
   * @param space Space to delete.
   * @param deleteChildren Whether to delete the Space's children as well.
   * @return Observable emitting true if the Space was deleted, otherwise false.
   */
  public deleteSpace(space: Space, deleteChildren: boolean): Observable<boolean> {
    const mutation = gql`
      mutation DeleteSpace($spaceId: String!, $deleteChildren: Boolean!) {
        deleteSpace(id: $spaceId, deleteChildren: $deleteChildren)
      }
    `;
    const variables = {spaceId: space.id, deleteChildren};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteSpace'] as boolean),
        mergeMap(success => {
          if (success) {
            this.deleteSpaceSubject.next({deletedSpace: space, deleteChildren});

            // Refresh parent Spaces
            return this.getRelatedSpacesById(space.parentPath.last())
              .pipe(
                tap(updatedSpaces => this.updateSpacesSubject.next(updatedSpaces)),
                thenReturn(true)
              );
          } else {
            return of(false);
          }
        })
      );
  }

  /**
   * Call the API to delete the given Spaces. If the Space has children, they are deleted as well
   * @param spaceIds Space's IDs to delete.
   * @return Observable emitting true if the Spaces was deleted.
   */
  public deleteSpaces(spaceIds: string[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteSpaces($spaceIds: [String!]!) {
        deleteSpaces(ids: $spaceIds)
      }
    `;
    const variables = {spaceIds};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteSpaces'] as boolean),
        tap(success => success && this.deleteSpacesSubject.next(spaceIds))
      );
  }

  /**
   * Navigate to the spaces tab of the Asset's sheet.
   * @param assetId Id of the Asset to navigate to.
   */
  public async navigateToAssetSheet(assetId: string): Promise<void> {
    await this.assetsService.navigateToAssetSheet(assetId, 'spaces');
  }

  /**
   * Load all the Documents related to a given Space.
   * @param spaceId ID of the Space the Documents are related to.
   * @return Observable emitting Documents linked to the Space.
   */
  public loadSpaceDocuments(spaceId: string): Observable<Document[]> {
    return this.documentsService.loadEntityDocuments(
      spaceId,
      EntityTypeEnum.SPACE,
      DocumentType.DOCUMENT
    );
  }

  /**
   * Open the side panel with information about the selected Space.
   * @param space Space to be displayed in the side panel.
   */
  public openSpaceSidePanel(space: Space): void {
    this.sidePanelToggleSubject.next(space);
  }

  /**
   * Close the side panel.
   */
  public closeSpaceSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }

  /**
   * Check whether the Space's parent path does not exceed the maximum allowed number of parents.
   * @param space Space to check.
   * @return True if the Space does not exceed the maximum number of parents, false if the limit is exceeded.
   */
  public isPathLengthValid(space: Space): boolean {
    // FIXME use maxSubspaceCount from organization once the configuration is added
    // this.appManager.currentOrganization.maxSubspaceCount;
    return space.parentPath.length < this.appConfig.MAX_SUBSPACE_COUNT;
  }

  /**
   * Return whether moving a Space to another parent is allowed. This does not actually move the Space,
   * use {@link updateSpace} for moving Spaces after checking using this method.
   * @param spaceId ID of the Space that needs to be moved.
   * @param parentPath ID of the intended new parent Space.
   * @param moveChildren Whether the move would also move the Space's children.
   * @return Null if move is allowed, error returned otherwise.
   */
  public canMoveSpace(spaceId: string, parentPath: string[], moveChildren: boolean): Observable<Error | null> {
    const query = gql`
      query CanMoveSpace($spaceId: String!, $parentPath: [String]!, $moveChildren: Boolean!) {
        canMoveSpace(id: $spaceId, parentPath: $parentPath, moveSpaceChildren: $moveChildren)
      }
    `;
    const variables = {spaceId, parentPath, moveChildren};

    return this.apiService.query({query, variables})
      .pipe(
        map(data => {
          const error = data['canMoveSpace'] as string;
          return error ? new Error(error) : null;
        })
      );
  }

  /**
   * Fetch all related spaces of a Space (including itself) from the API.
   * @param spaceId ID of the Space to look for parents.
   * @return Related Spaces.
   */
  private getRelatedSpacesById(spaceId: string): Observable<Space[]> {
    const selectParams = {
      filters: [
        orGroup(
          eq('id', spaceId),
          eq('parentPath', spaceId)
        )
      ]
    };
    return this.loadAllSpaces(selectParams);
  }

  /**
   * Return all Spaces that have been updated after moving a Space.
   * @param spaceId ID of the moved Space.
   * @param oldParentSpaceId ID of the Space's parent before it was moved.
   * @return Updated Spaces.
   */
  private getUpdatedSpacesAfterMovingSpace(spaceId: string, oldParentSpaceId: string): Observable<Space[]> {
    // TODO : TTT-2240 return updated with updateParentId method
    const selectParams = {
      filters: [
        orGroup(
          eq('id', spaceId),
          eq('parentPath', spaceId),
          eq('parentPath', oldParentSpaceId)
        )
      ]
    };
    return this.loadAllSpaces(selectParams);
  }
}
